import { useState, useMemo, useEffect } from 'react';
import { motion } from 'framer-motion';
import SubNav from '../../components/feature/SubNav';
import CategoryBar from './components/CategoryBar';
import PostCard from './components/PostCard';
import CommunitySidebar from './components/CommunitySidebar';
import WritePostModal from './components/WritePostModal';
import Pagination from '../jobs/components/Pagination';
import Footer from '../home/components/Footer';
import { communityCategories, communityPosts } from '../../mocks/community';
import KakaoFloatingButton from '../../components/feature/KakaoFloatingButton';
import { useSEO } from '../../utils/seo';

const POSTS_PER_PAGE = 6;

export default function CommunityPage() {
  const [scrolled, setScrolled] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'views'>('latest');
  const [currentPage, setCurrentPage] = useState(1);
  const [likedPosts, setLikedPosts] = useState<Set<string>>(new Set());
  const [isWriteOpen, setIsWriteOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useSEO({
    title: '커뮤니티 - 온니잡 | 유흥업계 소식과 정보',
    description: '나이트라이프 업계 종사자들의 생생한 경험담, 업계 소식, 취업 팁을 공유하는 커뮤니티. 온니잡에서 다양한 정보를 확인하세요.',
    keywords: '유흥업 커뮤니티, 유흥구인구직커뮤니티, 온니잡커뮤티,유흥여성전용커뮤니티,밤알바커뮤니티',
    canonical: `${siteUrl}/community`,
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'BreadcrumbList',
          '@id': `${siteUrl}/community#breadcrumb`,
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '홈', item: siteUrl },
            { '@type': 'ListItem', position: 2, name: '커뮤니티', item: `${siteUrl}/community` }
          ]
        },
        {
          '@type': 'DiscussionForumPosting',
          '@id': `${siteUrl}/community#forum`,
          name: '온니잡 커뮤니티',
          description: '나이트라이프 업계 소식과 인사이트를 공유하는 커뮤니티',
          url: `${siteUrl}/community`,
          inLanguage: 'ko-KR',
          isPartOf: { '@id': `${siteUrl}/#website` }
        },
        {
          '@type': 'CollectionPage',
          '@id': `${siteUrl}/community#webpage`,
          name: '커뮤니티 - 온니잡',
          description: '나이트라이프 업계 소식과 인사이트를 공유하는 커뮤니티',
          url: `${siteUrl}/community`,
          inLanguage: 'ko-KR',
          isPartOf: { '@id': `${siteUrl}/#website` },
          breadcrumb: { '@id': `${siteUrl}/community#breadcrumb` },
          mainEntity: {
            '@type': 'ItemList',
            name: '최신 커뮤니티 게시글',
            numberOfItems: communityPosts.length,
            itemListElement: communityPosts.slice(0, 6).map((post, index) => ({
              '@type': 'ListItem',
              position: index + 1,
              item: {
                '@type': 'Article',
                '@id': `${siteUrl}/post-detail/${post.id}`,
                headline: post.title,
                description: post.content.substring(0, 150),
                datePublished: post.createdAt,
                author: {
                  '@type': 'Person',
                  name: post.author
                },
                url: `${siteUrl}/post-detail/${post.id}`,
                interactionStatistic: [
                  {
                    '@type': 'InteractionCounter',
                    interactionType: 'https://schema.org/LikeAction',
                    userInteractionCount: post.likes
                  },
                  {
                    '@type': 'InteractionCounter',
                    interactionType: 'https://schema.org/ViewAction',
                    userInteractionCount: post.views
                  }
                ]
              }
            }))
          }
        }
      ]
    }
  });

  const filteredPosts = useMemo(() => {
    let result = [...communityPosts];

    if (selectedCategory !== 'all') {
      result = result.filter((p) => p.category === selectedCategory);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (p) =>
          p.title.toLowerCase().includes(q) ||
          p.content.toLowerCase().includes(q) ||
          p.author.toLowerCase().includes(q)
      );
    }

    if (sortBy === 'latest') {
      result.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    } else if (sortBy === 'popular') {
      result.sort((a, b) => b.likes - a.likes);
    } else {
      result.sort((a, b) => b.views - a.views);
    }

    return result;
  }, [selectedCategory, searchQuery, sortBy]);

  const totalPages = Math.max(1, Math.ceil(filteredPosts.length / POSTS_PER_PAGE));
  const paginatedPosts = filteredPosts.slice(
    (currentPage - 1) * POSTS_PER_PAGE,
    currentPage * POSTS_PER_PAGE
  );

  const handleLike = (id: string) => {
    setLikedPosts((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-dark-deep">
      <SubNav scrolled={scrolled} />

      {/* Hero */}
      <section className="relative pt-32 pb-14 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-neon-pink/6 via-transparent to-transparent"></div>
        <div className="container mx-auto px-10 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-10"
          >
            <span className="inline-block text-xs font-bold text-neon-green border border-neon-green/30 rounded-full px-4 py-1.5 mb-5">
              업계 종사자 전용
            </span>
            <h2 className="text-5xl font-display font-black text-white mb-4">
              <a href="/community">커뮤니티</a>
            </h2>
            <p className="text-base text-gray-400 max-w-lg mx-auto">
              나이트라이프 업계 소식과 인사이트를 공유하세요
            </p>
          </motion.div>

          {/* Search + Category */}
          <div className="max-w-3xl mx-auto space-y-5">
            <div className="relative">
              <i className="ri-search-line absolute left-5 top-1/2 -translate-y-1/2 text-gray-500 w-5 h-5 flex items-center justify-center"></i>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                placeholder="게시글 검색..."
                className="w-full bg-white/5 border border-white/10 rounded-2xl pl-13 pr-5 py-3.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-neon-purple/50 transition-colors backdrop-blur-md"
              />
            </div>
            <CategoryBar
              categories={communityCategories}
              selected={selectedCategory}
              onSelect={(id) => { setSelectedCategory(id); setCurrentPage(1); }}
            />
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="container mx-auto px-10 pb-20">
        <div className="flex gap-8">
          {/* Posts */}
          <div className="flex-1 min-w-0">
            {/* Sort Bar */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="flex items-center justify-between mb-8"
            >
              <p className="text-sm text-gray-400">
                총 <strong className="text-white">{filteredPosts.length}</strong>개의 게시글
              </p>
              <div className="flex items-center gap-1 bg-white/5 rounded-xl p-1">
                {[
                  { key: 'latest' as const, label: '최신순' },
                  { key: 'popular' as const, label: '인기순' },
                  { key: 'views' as const, label: '조회순' },
                ].map((s) => (
                  <button
                    key={s.key}
                    onClick={() => { setSortBy(s.key); setCurrentPage(1); }}
                    className={`px-4 py-2 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                      sortBy === s.key
                        ? 'bg-neon-purple text-white'
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </motion.div>

            {/* Grid */}
            {paginatedPosts.length > 0 ? (
              <div className="grid grid-cols-2 gap-5">
                {paginatedPosts.map((post, index) => (
                  <PostCard
                    key={post.id}
                    post={post}
                    index={index}
                    onLike={handleLike}
                    likedPosts={likedPosts}
                  />
                ))}
              </div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex flex-col items-center justify-center py-32"
              >
                <i className="ri-chat-3-line text-5xl text-gray-600 mb-4 w-14 h-14 flex items-center justify-center"></i>
                <p className="text-lg text-gray-500 mb-2">게시글이 없습니다</p>
                <p className="text-sm text-gray-600">첫 번째 글을 작성해보세요!</p>
              </motion.div>
            )}

            {/* Pagination */}
            {filteredPosts.length > POSTS_PER_PAGE && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            )}
          </div>

          {/* Sidebar */}
          <CommunitySidebar />
        </div>
      </main>

      {/* Floating Write Button */}
      <motion.button
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4, delay: 0.5 }}
        onClick={() => setIsWriteOpen(true)}
        className="fixed bottom-8 right-24 z-50 w-14 h-14 flex items-center justify-center rounded-full bg-gradient-neon text-white shadow-neon-purple hover:shadow-[0_0_30px_rgba(168,85,247,0.7)] transition-all cursor-pointer"
      >
        <i className="ri-edit-line text-xl w-6 h-6 flex items-center justify-center"></i>
      </motion.button>

      {/* Write Modal */}
      <WritePostModal isOpen={isWriteOpen} onClose={() => setIsWriteOpen(false)} />

      <KakaoFloatingButton />

      <Footer />
    </div>
  );
}
